import { defineStore } from 'pinia'
import useApi from '@/composables/useApi'
import { extractErrorMessage } from '@/utils/http'

const AUTH_STORAGE_KEY = 'authData'
const TOKEN_EXPIRY_LEEWAY_SECONDS = 5
let refreshPromise = null

function createInitialState() {
    return {
        id: '',
        eMail: '',
        role: '',
        firstName: '',
        lastName: '',
        preferredLocale: '',
        abbreviation: '',
        notes: '',
        createdAt: '',
        updatedAt: '',
        accessToken: '',
        accessTokenExpiresAt: 0
    }
}

function canUseLocalStorage() {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function extractId(payload = {}) {
    return payload?.id || payload?._id || ''
}

function normalizeText(value) {
    return typeof value === 'string' ? value : value == null ? '' : String(value)
}

function getNowUnix() {
    return Math.floor(Date.now() / 1000)
}

function decodeJwtExp(token = '') {
    try {
        if (typeof window === 'undefined' || typeof window.atob !== 'function') return 0
        const payload = token.split('.')[1]
        if (!payload) return 0
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
        const padding = (4 - (normalized.length % 4)) % 4
        const decoded = JSON.parse(window.atob(`${normalized}${'='.repeat(padding)}`))
        return Number(decoded?.exp) || 0
    } catch {
        return 0
    }
}


export const useAuthStore = defineStore('auth', {
    state: () => createInitialState(),
    getters: {
        user: (state) => state.id,
        isAccessTokenExpired: (state) => {
            if (!state.accessToken || !state.accessTokenExpiresAt) return true
            return state.accessTokenExpiresAt <= (getNowUnix() + TOKEN_EXPIRY_LEEWAY_SECONDS)
        },
        isAuthenticated: (state) => Boolean(state.id && state.accessToken && state.accessTokenExpiresAt > (getNowUnix() + TOKEN_EXPIRY_LEEWAY_SECONDS))
    },
    actions: {
        applyUserData(payload = {}) {
            this.id = extractId(payload)
            this.eMail = normalizeText(payload?.eMail).trim()
            this.role = normalizeText(payload?.role).trim()
            this.firstName = normalizeText(payload?.firstName).trim()
            this.lastName = normalizeText(payload?.lastName).trim()
            this.preferredLocale = normalizeText(payload?.preferredLocale).trim()
            this.abbreviation = normalizeText(payload?.abbreviation).trim()
            this.notes = normalizeText(payload?.notes).trim()
            this.createdAt = normalizeText(payload?.createdAt).trim()
            this.updatedAt = normalizeText(payload?.updatedAt).trim()
        },
        setAccessToken(token = '', expiresAt = 0) {
            this.accessToken = token || ''
            this.accessTokenExpiresAt = Number(expiresAt) || decodeJwtExp(token)
        },
        clearAuthState() {
            Object.assign(this, createInitialState())
        },
        saveAuthToStorage() {
            if (!canUseLocalStorage()) return
            const authData = {
                id: this.id || '',
                eMail: this.eMail || '',
                role: this.role || '',
                firstName: this.firstName || '',
                lastName: this.lastName || '',
                preferredLocale: this.preferredLocale || '',
                abbreviation: this.abbreviation || '',
                notes: this.notes || '',
                createdAt: this.createdAt || '',
                updatedAt: this.updatedAt || '',
                accessToken: this.accessToken || '',
                accessTokenExpiresAt: Number(this.accessTokenExpiresAt) || 0
            }
            window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData))
        },
        loadAuthFromStorage() {
            if (!canUseLocalStorage()) return
            const raw = window.localStorage.getItem(AUTH_STORAGE_KEY)
            if (!raw) return

            try {
                const authData = JSON.parse(raw)
                this.applyUserData(authData)
                this.setAccessToken(authData?.accessToken || '', authData?.accessTokenExpiresAt || 0)
                if (this.isAccessTokenExpired) {
                    this.clearAuthState()
                    this.clearAuthStorage()
                }
            } catch {
                window.localStorage.removeItem(AUTH_STORAGE_KEY)
            }
        },
        clearAuthStorage() {
            if (!canUseLocalStorage()) return
            window.localStorage.removeItem(AUTH_STORAGE_KEY)
        },
        async bootstrapAuth() {
            this.loadAuthFromStorage()

            if (!this.isAccessTokenExpired) {
                return
            }

            try {
                await this.refreshToken()
                await this.getUser()
            } catch {
                this.clearAuthState()
                this.clearAuthStorage()
            }
        },
        async login(payload = {}) {
            try {
                const response = await useApi().post('/api/v1/auth/login', payload)
                const data = response?.data || {}
                this.applyUserData(data)
                this.setAccessToken(data.accessToken || '', data.accessTokenExpiresAt || 0)
                this.saveAuthToStorage()
                return response
            } catch (error) {
                throw new Error(extractErrorMessage(error, 'Login failed'))
            }
        },
        async register(payload) {
            try {
                const response = await useApi().post('/api/v1/auth/register', payload)
                return response
            } catch (error) {
                throw new Error(extractErrorMessage(error, 'Registration failed'))
            }
        },
        async logout() {
            try {
                const response = await useApi().post('/api/v1/auth/logout')
                this.clearAuthState()
                this.clearAuthStorage()
                return response
            } catch (error) {
                throw new Error(extractErrorMessage(error, 'Logout failed'))
            }
        },
        async getUser(config) {
            try {
                const response = await useApi().get('/api/v1/auth/user', config)
                const userData = response?.data?.user || {}
                this.applyUserData(userData)
                this.saveAuthToStorage()
                return response
            } catch (error) {
                this.clearAuthState()
                this.clearAuthStorage()
                throw new Error(extractErrorMessage(error, 'User retrieval failed'))
            }
        },
        async ensureValidAccessToken() {
            if (!this.accessToken || !this.id) return
            if (!this.isAccessTokenExpired) return
            await this.refreshToken()
        },
        async refreshToken() {
            if (!refreshPromise) {
                refreshPromise = (async () => {
                    try {
                        const response = await useApi().post('/api/v1/auth/refresh')
                        const data = response?.data || {}
                        this.setAccessToken(data.accessToken || '', data.accessTokenExpiresAt || 0)
                        this.saveAuthToStorage()
                        return response
                    } catch (error) {
                        this.clearAuthState()
                        this.clearAuthStorage()
                        throw new Error(extractErrorMessage(error, 'Refresh token failed'))
                    } finally {
                        refreshPromise = null
                    }
                })()
            }

            return refreshPromise
        },
        async updatePreferredLocale(preferredLocale) {
            try {
                const response = await useApi().patch('/api/v1/auth/user/preferences', { preferredLocale })
                const userData = response?.data?.user || {}
                this.preferredLocale = userData.preferredLocale || preferredLocale || this.preferredLocale
                this.saveAuthToStorage()
                return response
            } catch (error) {
                throw new Error(extractErrorMessage(error, 'Failed to update preferred locale'))
            }
        }
    }
});
